import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Badge,
    Button,
    Combobox,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    makeStyles,
    Option,
    shorthands,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    tokens,
} from "@fluentui/react-components";
import {
    ArrowSync20Regular,
    Delete20Regular,
    Person20Regular,
} from "@fluentui/react-icons";
import { useAuth } from "../../context/AuthContext";
import { apiUrl } from "../../utils/api";
import {
    telephonyService,
    type ExtensionListItem,
    type ExtensionMappingDto,
} from "../../services/telephonyService";
import { AnrufHistorieReparieren } from "./AnrufHistorieReparieren";
import { useBestaetigen } from "../../components/redesign/bestaetigen";

interface KmvsUser {
    id: string;
    email: string | null;
    name: string | null;
    firstName: string | null;
    lastName: string | null;
}

const useStyles = makeStyles({
    root: {
        ...shorthands.padding("24px"),
        maxWidth: "1100px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
    },
    headerRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    syncResult: {
        ...shorthands.padding("8px", "12px"),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        backgroundColor: tokens.colorNeutralBackground2,
        fontSize: tokens.fontSizeBase200,
    },
    forbidden: {
        ...shorthands.padding("32px"),
        textAlign: "center",
        color: tokens.colorNeutralForeground2,
    },
});

const displayUser = (u: KmvsUser): string => {
    if (u.lastName && u.firstName) return `${u.lastName}, ${u.firstName}`;
    return u.name || u.email || u.id;
};

export const TelephonyExtensionsPage: React.FC = () => {
    const { bestaetigen } = useBestaetigen();
    const styles = useStyles();
    const { isAdmin, isGfgl } = useAuth();
    const canManage = isAdmin || isGfgl;

    const [extensions, setExtensions] = useState<ExtensionListItem[]>([]);
    const [mappings, setMappings] = useState<ExtensionMappingDto[]>([]);
    const [users, setUsers] = useState<KmvsUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<string | null>(null);

    const [assignFor, setAssignFor] = useState<ExtensionListItem | null>(null);
    const [assignUserId, setAssignUserId] = useState<string>("");

    const load = useCallback(async () => {
        if (!canManage) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const [exts, maps, usersRes] = await Promise.all([
                telephonyService.listExtensions(),
                telephonyService.listMappings(),
                fetch(apiUrl("users")).then(async (r) => {
                    if (!r.ok) throw new Error(`User-Liste fehlgeschlagen (${r.status})`);
                    return (await r.json()) as KmvsUser[];
                }),
            ]);
            setExtensions(exts);
            setMappings(maps);
            setUsers(usersRes);
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setLoading(false);
        }
    }, [canManage]);

    useEffect(() => {
        load();
    }, [load]);

    const mappingByExt = useMemo(() => {
        const m = new Map<string, ExtensionMappingDto>();
        for (const map of mappings) m.set(map.extension, map);
        return m;
    }, [mappings]);

    const userById = useMemo(() => {
        const m = new Map<string, KmvsUser>();
        for (const u of users) m.set(u.id, u);
        return m;
    }, [users]);

    const onSync = async () => {
        setSyncing(true);
        setSyncResult(null);
        setError(null);
        try {
            const res = await telephonyService.syncExtensions();
            setSyncResult(
                `${res.fetchedFromPbx} Extensions geholt · ${res.matchedByEmail} per E-Mail gematched · ${res.upserted} Mappings aktualisiert. ` +
                    (res.unmatchedExtensions.length > 0
                        ? `Ohne KMVS-User: ${res.unmatchedExtensions.join(", ")}`
                        : "Alle Extensions zugeordnet."),
            );
            await load();
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setSyncing(false);
        }
    };

    const onAssignConfirm = async () => {
        if (!assignFor || !assignUserId) return;
        try {
            await telephonyService.assignExtension({
                extension: assignFor.number,
                userId: assignUserId,
                displayName: assignFor.displayName ?? undefined,
                email: assignFor.email ?? undefined,
            });
            setAssignFor(null);
            setAssignUserId("");
            await load();
        } catch (e) {
            setError((e as Error).message);
        }
    };

    const onRemoveMapping = async (mappingId: number) => {
        const ok = await bestaetigen({
            titel: "Zuordnung entfernen?",
            text: "Die Nebenstelle ist danach keinem Nutzer mehr zugeordnet.",
            ja: "Entfernen",
            ton: "danger",
        });
        if (!ok) return;
        try {
            await telephonyService.removeMapping(mappingId);
            await load();
        } catch (e) {
            setError((e as Error).message);
        }
    };

    if (!canManage) {
        return (
            <div className={styles.root}>
                <div className={styles.forbidden}>
                    Diese Seite ist nur für Admins und Geschäftsleitung sichtbar.
                </div>
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <div className={styles.headerRow}>
                <Text size={700} weight="semibold">
                    Telefonie · Extension-Zuordnung
                </Text>
                <Button
                    appearance="primary"
                    icon={<ArrowSync20Regular />}
                    onClick={onSync}
                    disabled={syncing}
                >
                    {syncing ? "Sync läuft …" : "Vom PBX synchronisieren"}
                </Button>
            </div>

            {syncResult && <div className={styles.syncResult}>{syncResult}</div>}
            {error && (
                <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Text>
            )}

            {/* Einmalige Reparatur der Altbestände — steht bewusst hier, weil sie
                erst nach einem PBX-Sync sinnvoll ist (der zieht die Teams mit). */}
            <AnrufHistorieReparieren />

            {loading ? (
                <Spinner label="Lade Extensions …" />
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>Extension</TableHeaderCell>
                            <TableHeaderCell>Anzeigename (PBX)</TableHeaderCell>
                            <TableHeaderCell>E-Mail (PBX)</TableHeaderCell>
                            <TableHeaderCell>Status</TableHeaderCell>
                            <TableHeaderCell>Zugeordneter Nutzer</TableHeaderCell>
                            <TableHeaderCell></TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {extensions.map((ext) => {
                            const map = mappingByExt.get(ext.number);
                            const user = map ? userById.get(map.userId) : undefined;
                            return (
                                <TableRow key={ext.number}>
                                    <TableCell>{ext.number}</TableCell>
                                    <TableCell>{ext.displayName ?? "—"}</TableCell>
                                    <TableCell>{ext.email ?? "—"}</TableCell>
                                    <TableCell>
                                        <Badge
                                            appearance="tint"
                                            color={ext.online ? "success" : "subtle"}
                                        >
                                            {ext.online ? "online" : "offline"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        {map ? (
                                            user ? (
                                                displayUser(user)
                                            ) : (
                                                <Text size={200}>{map.userId}</Text>
                                            )
                                        ) : (
                                            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                                                — nicht zugeordnet —
                                            </Text>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            appearance="subtle"
                                            icon={<Person20Regular />}
                                            onClick={() => {
                                                setAssignFor(ext);
                                                setAssignUserId(map?.userId ?? "");
                                            }}
                                        >
                                            Zuweisen
                                        </Button>
                                        {map && (
                                            <Button
                                                size="small"
                                                appearance="subtle"
                                                icon={<Delete20Regular />}
                                                onClick={() => onRemoveMapping(map.id)}
                                                title="Mapping entfernen"
                                                aria-label="Mapping entfernen"
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}

            <Dialog
                open={!!assignFor}
                onOpenChange={(_, data) => {
                    if (!data.open) {
                        setAssignFor(null);
                        setAssignUserId("");
                    }
                }}
            >
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>
                            Extension {assignFor?.number} zuweisen
                        </DialogTitle>
                        <DialogContent>
                            <Combobox
                                placeholder="Nutzer suchen …"
                                onOptionSelect={(_, data) => setAssignUserId(String(data.optionValue ?? ""))}
                                value={
                                    assignUserId
                                        ? displayUser(userById.get(assignUserId) ?? ({ id: assignUserId } as KmvsUser))
                                        : ""
                                }
                            >
                                {users.map((u) => (
                                    <Option key={u.id} value={u.id} text={displayUser(u)}>
                                        {displayUser(u)} {u.email ? `· ${u.email}` : ""}
                                    </Option>
                                ))}
                            </Combobox>
                        </DialogContent>
                        <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                                <Button appearance="secondary">Abbrechen</Button>
                            </DialogTrigger>
                            <Button
                                appearance="primary"
                                onClick={onAssignConfirm}
                                disabled={!assignUserId}
                            >
                                Zuweisen
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default TelephonyExtensionsPage;
